import { useRef, useState, type PointerEvent as ReactPointerEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react'

interface StarRatingProps {
  value: number
  onChange?: (value: number) => void
  size?: number
  readOnly?: boolean
  ariaLabel?: string
}

const STAR_COUNT = 5
const STEP = 0.5
const STAR_PATH = 'M8 1l2.2 4.5 4.8.7-3.5 3.4.8 4.8L8 12l-4.3 2.4.8-4.8L1 6.2l4.8-.7z'

function snapValue(ratio: number): number {
  const clamped = Math.max(0, Math.min(1, ratio))
  const raw = clamped * STAR_COUNT
  // Round up to the nearest 0.5 so the left edge of a star = 0.5, right edge = 1.0
  const snapped = Math.ceil(raw / STEP) * STEP
  return Math.max(STEP, Math.min(STAR_COUNT, snapped))
}

export function StarRating({ value, onChange, size = 14, readOnly = false, ariaLabel }: StarRatingProps) {
  const [preview, setPreview] = useState<number | null>(null)
  const stripRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)

  const interactive = !readOnly && !!onChange
  const display = preview ?? value ?? 0

  function valueFromClientX(clientX: number): number {
    const el = stripRef.current
    if (!el) return 0
    const rect = el.getBoundingClientRect()
    return snapValue((clientX - rect.left) / rect.width)
  }

  function handlePointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (!interactive) return
    e.currentTarget.setPointerCapture(e.pointerId)
    draggingRef.current = true
    setPreview(valueFromClientX(e.clientX))
  }

  function handlePointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!interactive) return
    if (e.pointerType === 'mouse' || draggingRef.current) {
      setPreview(valueFromClientX(e.clientX))
    }
  }

  function handlePointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    if (!interactive || !draggingRef.current) return
    const next = valueFromClientX(e.clientX)
    onChange!(next === value ? 0 : next)
    draggingRef.current = false
    if (e.pointerType !== 'mouse') setPreview(null)
  }

  function handlePointerLeave() {
    if (!draggingRef.current) setPreview(null)
  }

  function handlePointerCancel() {
    // Browser hijacked the gesture (e.g. vertical pan turned into a scroll).
    // Drop drag state without committing — otherwise a scroll-from-stars
    // commits whatever rating happened to be under the finger.
    draggingRef.current = false
    setPreview(null)
  }

  function handleKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (!interactive) return
    let next: number | null = null
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = Math.min(STAR_COUNT, (value || 0) + STEP)
        break
      case 'ArrowLeft':
      case 'ArrowDown':
        next = Math.max(0, (value || 0) - STEP)
        break
      case 'Home':
        next = STEP
        break
      case 'End':
        next = STAR_COUNT
        break
    }
    if (next !== null) {
      e.preventDefault()
      onChange!(next)
    }
  }

  return (
    <span className="cg-stars-wrap" onClick={(e) => e.stopPropagation()}>
      <div
        ref={stripRef}
        className="cg-stars"
        data-readonly={readOnly}
        data-active={preview !== null}
        role={interactive ? 'slider' : undefined}
        aria-label={ariaLabel ?? 'Rating'}
        aria-valuemin={interactive ? 0 : undefined}
        aria-valuemax={interactive ? STAR_COUNT : undefined}
        aria-valuenow={interactive ? value : undefined}
        tabIndex={interactive ? 0 : undefined}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerLeave={handlePointerLeave}
        onKeyDown={handleKeyDown}
        style={{ fontSize: size }}
      >
        {Array.from({ length: STAR_COUNT }, (_, i) => {
          const fill = Math.max(0, Math.min(1, display - i))
          return (
            <span key={i} className="cg-star">
              <span className="cg-star-glyph">
                <svg className="cg-star-bg" viewBox="0 0 16 16" aria-hidden="true">
                  <path d={STAR_PATH} fill="currentColor" />
                </svg>
                <span className="cg-star-fg" style={{ width: `${fill * 100}%` }} aria-hidden="true">
                  <svg viewBox="0 0 16 16" preserveAspectRatio="xMinYMid meet">
                    <path d={STAR_PATH} fill="currentColor" />
                  </svg>
                </span>
              </span>
            </span>
          )
        })}
      </div>
      {interactive && preview !== null && (
        <span className="cg-stars-preview">{preview.toFixed(1)}</span>
      )}
    </span>
  )
}
